import React from "react";

/** Unicode subscript digits, for contexts that need a plain string. */
const SUBSCRIPT_DIGITS = "₀₁₂₃₄₅₆₇₈₉";
const toSubscriptDigits = (n) =>
  String(n)
    .split("")
    .map((d) => SUBSCRIPT_DIGITS[Number(d)] ?? d)
    .join("");

/**
 * Shared core for both subscript formatters, so the JSX and plain-string
 * variants can never drift apart on threshold or rounding.
 *
 * Subscript kicks in at MORE THAN 2 leading zeros after the decimal, i.e.
 * anything below 0.001 (0.000xyz and smaller). At exactly 2 zeros (0.00xyz)
 * the plain form is still short enough to read, so it stays literal; below
 * that the zeros stop being countable at a glance and the subscript is what
 * makes the magnitude legible — the threshold CoinGecko-style tickers use.
 *
 * @returns {{plain: string} | {zeros: number, sig: string}}
 */
const subscriptParts = (abs, sigDigits) => {
  if (abs >= 0.001) {
    let d = 2;
    if (abs < 1) { d = 6; if (abs < 0.01) d = 8; }
    return { plain: abs.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d }) };
  }

  // Round at the significant digit rather than slicing `toFixed(20)`. That
  // slice TRUNCATED binary-float noise — 0.000207 stringifies as
  // "0.00020699999999999999", so taking 3 digits printed 206, a number the
  // user never had. `toFixed(zeros + sigDigits)` rounds instead.
  const leading = Math.max(0, -Math.floor(Math.log10(abs)) - 1);
  const dec = abs.toFixed(leading + sigDigits).split(".")[1] || "";

  // Re-count zeros from the ROUNDED string: rounding can carry and remove one
  // (0.0009999 → "0.001000", 3 leading zeros becomes 2).
  let zeros = 0;
  while (zeros < dec.length && dec[zeros] === "0") zeros++;

  const sig = dec.slice(zeros).replace(/0+$/, "") || "0";
  return { zeros, sig };
};

/**
 * CoinGecko-style subscript zero format for very small numbers.
 * e.g. 0.00000018313 → 0.0₇1831
 * Works for prices, rates, percentages — anything with many leading zeros.
 *
 * @param {number|string} value - The number to format
 * @param {object} options
 * @param {string} options.prefix - Prefix like "$" (default: "")
 * @param {string} options.suffix - Suffix like "%" (default: "")
 * @param {number} options.sigDigits - Significant digits after zeros (default: 4)
 * @returns {JSX.Element|string}
 */
export const formatWithSubscript = (value, { prefix = "", suffix = "", sigDigits = 4 } = {}) => {
  if (!value && value !== 0) return `${prefix}0.00${suffix}`;

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return `${prefix}0.00${suffix}`;

  const abs = Math.abs(num);
  if (abs === 0) return `${prefix}0${suffix}`;

  const parts = subscriptParts(abs, sigDigits);
  if (parts.plain) return `${prefix}${parts.plain}${suffix}`;

  return (
    <>{prefix}0.0<sub style={{ fontSize: "0.75em" }}>{parts.zeros}</sub>{parts.sig}{suffix}</>
  );
};

/**
 * Plain-string twin of `formatWithSubscript`, using Unicode subscript digits
 * instead of a <sub> element — for callers that need a string rather than
 * JSX (e.g. `NumberTicker`'s `text` prop, `title`/`aria-label` attributes).
 * Identical threshold and rounding, since both share `subscriptParts`.
 *
 * @returns {string} e.g. "$0.0₇1831"
 */
export const formatWithSubscriptString = (value, { prefix = "", suffix = "", sigDigits = 4 } = {}) => {
  if (!value && value !== 0) return `${prefix}0.00${suffix}`;

  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return `${prefix}0.00${suffix}`;

  const abs = Math.abs(num);
  if (abs === 0) return `${prefix}0${suffix}`;

  const parts = subscriptParts(abs, sigDigits);
  if (parts.plain) return `${prefix}${parts.plain}${suffix}`;

  return `${prefix}0.0${toSubscriptDigits(parts.zeros)}${parts.sig}${suffix}`;
};

/**
 * CoinGecko-style subscript format specifically for prices (with $ prefix).
 * For low-price tokens: uses subscript zeros when price < 1
 * For regular tokens: standard formatting
 *
 * @param {number|string} priceValue - The price
 * @param {boolean} isLowPriceToken - Whether to use subscript format
 * @returns {JSX.Element|string}
 */
export const formatPriceWithSubscript = (priceValue, isLowPriceToken) => {
  if (!priceValue && priceValue !== 0) return "0.00";

  const num = parseFloat(priceValue);
  if (isNaN(num)) return "0.00";

  const abs = Math.abs(num);

  // For low-price tokens, use subscript-zero notation when price < 1
  if (isLowPriceToken && abs > 0 && abs < 1) {
    const raw = abs.toFixed(20);
    const decimalPart = raw.split(".")[1] || "";

    let zeroCount = 0;
    for (let i = 0; i < decimalPart.length; i++) {
      if (decimalPart[i] === "0") {
        zeroCount += 1;
      } else {
        break;
      }
    }

    const remaining = decimalPart.slice(zeroCount).slice(0, 3);

    if (!remaining) {
      return num.toFixed(8);
    }

    return (
      <>
        0.0<sub style={{ fontSize: "0.75em" }}>{zeroCount}</sub>{remaining}
      </>
    );
  }

  // For values >= 1 on low-priced tokens, show 6 decimals
  if (isLowPriceToken && abs >= 1) {
    return num.toFixed(6);
  }

  // For regular tokens, use standard formatting
  return Number(num).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

export const formatNumber = (value, options = {}) => {
    const {
      minDecimals = 2,
      maxDecimals = 6,
      currency = false,
      compact = false
    } = options;
  
    if (!value && value !== 0) return '-';
    
    const num = parseFloat(value);
    
    // Handle very small numbers
    if (Math.abs(num) < 0.000001 && num !== 0) {
      return num.toExponential(4);
    }
  
    // Handle regular numbers
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: maxDecimals,
      notation: compact ? 'compact' : 'standard',
      style: currency ? 'currency' : 'decimal',
      currency: 'USD'
    });
  
    return formatter.format(num);
  };

/**
 * Format price to show enough decimals until a non-zero digit is visible
 * This is useful for tokens with very small values like NIBI (0.004098)
 * @param {number|string} price - The price to format
 * @param {number} maxDecimals - Maximum decimals to show (default: 8)
 * @param {boolean} includeCurrency - Whether to include $ prefix (default: true)
 * @returns {string} Formatted price string
 */
export const formatPriceWithSignificantDecimals = (price, maxDecimals = 8, includeCurrency = true) => {
  if (!price && price !== 0) return includeCurrency ? "$0.00" : "0.00";
  
  const num = parseFloat(price);
  if (isNaN(num)) return includeCurrency ? "$0.00" : "0.00";
  
  const abs = Math.abs(num);
  
  // If the value is effectively 0 (after checking max decimals), return $0.00
  if (abs < Math.pow(10, -maxDecimals)) {
    return includeCurrency ? "$0.00" : "0.00";
  }
  
  // For prices >= 1, use standard 2 decimal format
  if (abs >= 1) {
    const formatted = num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return includeCurrency ? `$${formatted}` : formatted;
  }
  
  // For prices < 1, find the first non-zero digit
  // Start with 2 decimals and increase until we see a non-zero value
  let decimals = 2;
  while (decimals < maxDecimals && Number(abs.toFixed(decimals)) === 0) {
    decimals += 1;
  }
  
  // If after checking all decimals it's still 0, return $0.00
  if (Number(abs.toFixed(maxDecimals)) === 0) {
    return includeCurrency ? "$0.00" : "0.00";
  }
  
  // Format with the determined number of decimals
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  return includeCurrency ? `$${formatted}` : formatted;
};